import {gql} from '@apollo/client';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcessUserDisplay} from 'processes/chip-utils/getDefaultProcessTaskChipPopoverContent';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import {
  RequestV2SubmissionChipPopoverQuery,
  RequestV2SubmissionChipPopoverQueryVariables,
  RequestV2SubmissionChipPopoverQuery_requestV2Submission as RequestSubmissionOrInaccessibleGql,
} from 'processes/requests_v2/chips/__generated/RequestV2SubmissionChipPopoverQuery';
import RequestV2StatusDisplay from 'processes/requests_v2/RequestV2StatusDisplay';
import ChipPopoverContent from 'ui-library/chips/ChipPopoverContent';
import QueryingChipPopoverContent from 'ui-library/chips/QueryingChipPopoverContent';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

export const REQUEST_V2_SUBMISSION_CHIP_POPOVER_QUERY = gql`
  query RequestV2SubmissionChipPopoverQuery($requestV2SubmissionId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    requestV2Submission(id: $requestV2SubmissionId) {
      ... on RequestV2SubmissionNode {
        id
        name
        displayId
        definition {
          ... on RequestV2DefinitionNode {
            id
            name
          }
          ...InaccessibleInfoGraphQL
        }
        status
        folder {
          id
          source {
            id
            name
          }
        }
        creator {
          id
          name
          handle
          avatarUrl
        }
        createdAt
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function RequestV2SubmissionChipPopoverContent({
  requestV2Submission,
}: {
  requestV2Submission: RequestSubmissionOrInaccessibleGql;
}): JSX.Element {
  if (isInaccessible(requestV2Submission)) {
    return <>({NO_ACCESS_TEXT})</>;
  }

  const fields = [
    {
      label: 'Name',
      value: (
        <>
          <BenchlingIcon icon={ModelTypeConstants.REQUEST_V2_SUBMISSION} />
          &nbsp;{requestV2Submission.name}
        </>
      ),
    },
    {
      label: 'ID',
      value: requestV2Submission.displayId,
    },
    {
      label: 'Status',
      value: <RequestV2StatusDisplay status={requestV2Submission.status} />,
    },
    {
      label: 'Request template',
      value: isInaccessible(requestV2Submission.definition)
        ? NO_ACCESS_TEXT
        : requestV2Submission.definition.name,
    },
    {
      label: 'Project',
      value: (
        <>
          <BenchlingIcon icon={ModelTypeConstants.PROJECT} />
          &nbsp;{requestV2Submission.folder.source.name}
        </>
      ),
    },
    {
      label: 'Creator',
      value: <ProcessUserDisplay user={requestV2Submission.creator} />,
    },
    {label: 'Created at', value: readableTime(requestV2Submission.createdAt, {showTime: true})},
  ];

  return <ChipPopoverContent info={{fields}} />;
}

export default function QueryingRequestV2SubmissionChipPopoverContent(props: {id: string}): JSX.Element {
  return (
    <QueryingChipPopoverContent<
      RequestV2SubmissionChipPopoverQuery,
      RequestV2SubmissionChipPopoverQueryVariables
    >
      variables={{requestV2SubmissionId: props.id}}
      query={REQUEST_V2_SUBMISSION_CHIP_POPOVER_QUERY}
    >
      {(data: RequestV2SubmissionChipPopoverQuery) => (
        <RequestV2SubmissionChipPopoverContent requestV2Submission={data.requestV2Submission} />
      )}
    </QueryingChipPopoverContent>
  );
}
